import { formatNumber } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import { switchMap } from "rxjs";
import { AddOrDeleteAsinsActionEnum } from "@front/m19-api-client";
import { CurrencySymbols, Marketplaces } from "@front/m19-models";
import { AccountSelectionService, AsinService, AuthService, UserSelectionService } from "@front/m19-services";
import { ITab, ITabsComponent, ModalService, ToasterService } from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { LoadingService } from "@m19-board/services/loading.service";
import { AsinFilterService } from "@m19-board/shared/filter/asin-filter.service";
import { CustomFieldSettingsComponent } from "../custom-field-settings.component";
import { ManagedCatalogPageComponent } from "./catalog-page/managed-catalog-page.component";
import { UnmanagedCatalogPageComponent } from "./catalog-page/unmanaged-catalog-page.component";

export enum ProductTagField {
  FIELD1 = "field1",
  FIELD2 = "field2",
}

export enum CatalogManagementMode {
  AUTO = "auto",
  MANUAL = "manual",
}

export enum CatalogTab {
  MANAGED,
  UNMANAGED,
}

export const MANAGED_LIMIT = 20000;

export enum CsvSeedType {
  SEED = "SEED",
  BLACKLIST = "BLACKLIST",
}

export enum CsvMatchType {
  targetedProduct = "targetedProduct",
  exact = "exact",
  phrase = "phrase",
}

export const CATALOG_DEFAULT_PAGE_SIZE = 25;

@Component({
  selector: "app-catalog",
  imports: [
    ITabsComponent,
    TranslocoDirective,
    FormsModule,
    ManagedCatalogPageComponent,
    UnmanagedCatalogPageComponent,
  ],
  templateUrl: "./catalog.component.html",
  providers: [AsinFilterService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CatalogComponent {
  readonly translocoService = inject(TranslocoService);
  readonly accountSelectionService = inject(AccountSelectionService);
  readonly asinService = inject(AsinService);
  readonly authService = inject(AuthService);
  readonly modalService = inject(ModalService);
  readonly toastrService = inject(ToasterService);
  readonly loadingService = inject(LoadingService);
  readonly userSelectionService = inject(UserSelectionService);
  private static readonly LargeUnmanagedCatalogThreshold = 50_000;

  readonly CatalogTab = CatalogTab;
  readonly ProductTagField = ProductTagField;
  readonly AddOrDeleteAsinsActionEnum = AddOrDeleteAsinsActionEnum;
  readonly CatalogManagementMode = CatalogManagementMode;

  readonly exportManagedMenuItems = [];
  readonly bulkManageCatalogModeItems = [];
  private readonly accountMarketplace$ = this.accountSelectionService.singleAccountMarketplaceSelection$;
  readonly accountMarketplace = toSignal(this.accountMarketplace$, { requireSync: true });

  readonly locale = toSignal(this.authService.userLocale$, { requireSync: true });
  readonly currency = toSignal(this.userSelectionService.selectedCurrency$, { requireSync: true });
  readonly currencyCode = computed(() => Marketplaces[this.accountMarketplace()!.marketplace].currency);
  readonly currencyEx = computed(() => CurrencySymbols[this.currencyCode()]);
  readonly currencySymbol = computed(() => this.currencyEx());

  readonly inventorySize = toSignal(
    this.accountMarketplace$.pipe(
      switchMap((accountMarketplace) =>
        this.asinService.getInventorySize(accountMarketplace.accountId, accountMarketplace.marketplace),
      ),
    ),
  );

  readonly catalogTabs = computed((): ITab<CatalogTab>[] => {
    const size = this.inventorySize();
    const locale = this.locale();
    const unmanagedIsLarge = (size?.unmanaged ?? 0) > CatalogComponent.LargeUnmanagedCatalogThreshold;
    return [
      {
        label: this.translocoService.translate("catalog-page.managed_products"),
        value: CatalogTab.MANAGED,
        badge: size ? formatNumber(size.managed, locale, "1.0-0") : undefined,
      },
      {
        label: this.translocoService.translate("catalog-page.unmanaged_products"),
        value: CatalogTab.UNMANAGED,
        badge: size ? formatNumber(size.unmanaged, locale, "1.0-0") : undefined,
        badgeIcon: unmanagedIsLarge ? "alertTriangle" : undefined,
        badgeColor: unmanagedIsLarge ? "orange" : undefined,
        badgeTooltip: unmanagedIsLarge
          ? this.translocoService.translate("catalog-page.unmanaged_products_large_warning")
          : undefined,
      },
    ];
  });

  readonly isReadOnly = toSignal(this.accountSelectionService.readOnlyMode$);
  selectedTab = signal<CatalogTab>(CatalogTab.MANAGED);
  search = signal<string>("");

  openProductTagSettings() {
    this.modalService.openModal(CustomFieldSettingsComponent, {
      modalTitle: this.translocoService.translate("catalog-page.custom_field_settings"),
    });
  }

  activeTabChanged(tab: CatalogTab) {
    this.selectedTab.set(tab);
  }
}
